import { Fragment, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock3, Download, Minus, Scale } from 'lucide-react';
import { formatPersonName } from '../../utils/formatName';
import { STATUS_META } from '../../utils/statusMeta';
import { OrganizationCoverage } from '../../utils/dashboardProgress';
import { ComplianceSummary, ObligationState, SupplierRisk, UPCOMING_WINDOW_DAYS } from './compliance';
import { OBLIGATION_BY_ID, ObligationId, OBLIGATIONS } from './legalObligations';
import { AsyncState } from './useAsyncData';
import { Card, controlClass, DataState, daysLabel, linkClass, Meter, Pill } from './ui';

const number = (value: number) => value.toLocaleString('es-MX', { maximumFractionDigits: 1 });
const dayMonth = (value: string) => new Date(value + 'T12:00:00Z').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const COLUMNS: ObligationId[] = ['isr', 'iva', 'repse', 'opiniones', 'seguridad_social', 'expediente'];
const HEADLINE: ObligationId[] = ['isr', 'iva', 'repse', 'opiniones'];
const SUPPLIER_LIMIT = 15;
const expedienteUrl = (id: string, returnTo: string) => `/review/${encodeURIComponent(id)}?${new URLSearchParams({ returnTo })}`;
const DETAIL_LIMIT = 12;
const itemWeight = (item: SupplierRisk['items'][number]) => Math.max(...item.obligations.map((o) => OBLIGATION_BY_ID[o].weight));

const STATE_META: Record<ObligationState, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  ok: { label: 'Al corriente', className: 'text-emerald-700', Icon: CheckCircle2 },
  in_review: { label: 'En revisión', className: 'text-blue-700', Icon: Clock3 },
  attention: { label: 'Requiere atención', className: 'text-red-700', Icon: AlertTriangle },
  none: { label: 'No aplica', className: 'text-slate-400', Icon: Minus },
};

function StateIcon({ state, obligation }: { state: ObligationState; obligation: ObligationId }) {
  const meta = STATE_META[state];
  return (
    <span className={`inline-flex ${meta.className}`} title={`${OBLIGATION_BY_ID[obligation].title}: ${meta.label}`}>
      <meta.Icon className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">{meta.label}</span>
    </span>
  );
}

function Exposure({ data, total, selected, onSelect }: { data: ComplianceSummary; total: number; selected: ObligationId | null; onSelect: (id: ObligationId | null) => void }) {
  return (
    <section aria-labelledby="exposure-title">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="exposure-title" className="text-base font-semibold text-slate-900">Exposición legal</h2>
        <p className="text-xs text-slate-500">Proveedores con rechazos, correcciones o vencidos · de {number(total)} activos</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {HEADLINE.map((id) => {
          const obligation = OBLIGATION_BY_ID[id];
          const count = data.exposure[id];
          const active = selected === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(active ? null : id)}
              className={`flex min-w-0 flex-col rounded-xl border bg-white p-4 text-left transition-colors hover:border-slate-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 motion-reduce:transition-none ${active ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200'}`}
            >
              <span className="text-sm font-medium text-slate-600">{obligation.title}</span>
              <span className={`mt-2 flex items-baseline gap-2 text-3xl font-semibold tabular-nums ${count > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {number(count)}
                <span className="text-sm font-normal text-slate-500">{count === 1 ? 'proveedor' : 'proveedores'}</span>
              </span>
              <span className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-600">{obligation.consequence}</span>
              <span className="mt-auto pt-3 text-[11px] font-medium uppercase tracking-wide text-slate-400">{obligation.legalBasis}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function exportCsv(rows: SupplierRisk[]) {
  const escape = (value: string | number | null) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const header = ['Proveedor', 'Correo', 'Organización', 'Puntaje de riesgo', ...COLUMNS.map((id) => OBLIGATION_BY_ID[id].title), 'Pendientes de atención', 'Días sin resolver', 'Detalle'];
  const lines = rows.map((r) => [
    formatPersonName(r.name), r.email, r.organization || 'Sin organización', r.score,
    ...COLUMNS.map((id) => STATE_META[r.obligations[id]].label),
    r.items.length, r.oldestOpenDays,
    r.items.map((i) => `${i.documentName} ${i.period}: ${STATUS_META[i.status].label}`).join(' | '),
  ].map(escape).join(','));
  const blob = new Blob([`﻿${[header.map(escape).join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cumplimiento-proveedores-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function SupplierTable({ rows, filter, returnTo }: { rows: SupplierRisk[]; filter: ObligationId | null; returnTo: string }) {
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const visible = showAll ? rows : rows.slice(0, SUPPLIER_LIMIT);
  return (
    <Card
      labelledBy="suppliers-title"
      title="Proveedores por riesgo"
      subtitle={filter ? `Filtrado por ${OBLIGATION_BY_ID[filter].title.toLowerCase()}` : 'Ordenado por riesgo legal'}
      action={
        <button type="button" onClick={() => exportCsv(rows)} className={`${controlClass} inline-flex items-center gap-2`} disabled={rows.length === 0}>
          <Download className="h-4 w-4" aria-hidden="true" />Exportar CSV
        </button>
      }
    >
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-sm text-slate-600">{filter ? 'Ningún proveedor tiene pendientes en esta obligación.' : 'No hay proveedores activos.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <caption className="sr-only">Estado de cada proveedor por obligación legal</caption>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium text-slate-500">
                <th scope="col" className="px-5 py-2.5">Proveedor</th>
                {COLUMNS.map((id) => <th key={id} scope="col" className="px-2 py-2.5 text-center" title={OBLIGATION_BY_ID[id].title}>{OBLIGATION_BY_ID[id].short}</th>)}
                <th scope="col" className="px-3 py-2.5 text-right">Pendientes</th>
                <th scope="col" className="px-3 py-2.5 text-right">Sin resolver</th>
                <th scope="col" className="px-5 py-2.5"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => {
                const expanded = open === r.id;
                const name = formatPersonName(r.name);
                return (
                  <Fragment key={r.id}>
                    <tr className="align-middle hover:bg-slate-50">
                      <th scope="row" className="max-w-[280px] px-5 py-3 text-left font-normal">
                        <span className="block truncate font-medium text-slate-900">{name}</span>
                        <span className="block truncate text-xs text-slate-500">{r.organization || 'Sin organización'}</span>
                      </th>
                      {COLUMNS.map((id) => <td key={id} className="px-2 py-3 text-center"><StateIcon state={r.obligations[id]} obligation={id} /></td>)}
                      <td className="px-3 py-3 text-right tabular-nums">
                        {r.items.length > 0 ? (
                          <button type="button" className={linkClass} aria-expanded={expanded} onClick={() => setOpen(expanded ? null : r.id)}>
                            {number(r.items.length)}<span className="sr-only"> pendientes de {name}; mostrar detalle</span>
                          </button>
                        ) : <span className="text-slate-400">0</span>}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${r.oldestOpenDays != null && r.oldestOpenDays > 30 ? 'font-semibold text-red-700' : 'text-slate-600'}`}>
                        {r.oldestOpenDays == null ? '—' : daysLabel(r.oldestOpenDays)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link to={expedienteUrl(r.id, returnTo)} className={`${linkClass} whitespace-nowrap text-sm`} aria-label={`Abrir expediente de ${name}`}>Expediente</Link>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={COLUMNS.length + 4} className="px-5 pb-4 pt-1">
                          <ul className="grid gap-2 sm:grid-cols-2">
                            {[...r.items].sort((a, b) => itemWeight(b) - itemWeight(a)).slice(0, DETAIL_LIMIT).map((item) => (
                              <li key={item.key} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium text-slate-900">{item.documentName}</span>
                                  <Pill tone={item.status === 'correction_requested' ? 'watch' : 'bad'}>{STATUS_META[item.status].label}</Pill>
                                </div>
                                <p className="mt-0.5 text-xs text-slate-500">{item.period} · {item.obligations.map((o) => OBLIGATION_BY_ID[o].short).join(', ')}{item.deadline ? ` · límite ${dayMonth(item.deadline)}` : ''}</p>
                                {item.reason && <p className="mt-1 line-clamp-2 text-xs text-slate-600">Motivo: {item.reason}</p>}
                              </li>
                            ))}
                          </ul>
                          {r.items.length > DETAIL_LIMIT && (
                            <p className="mt-2 text-xs text-slate-500">
                              Y {number(r.items.length - DETAIL_LIMIT)} más. <Link to={expedienteUrl(r.id, returnTo)} className={linkClass}>Abrir el expediente completo</Link>
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
        <span className="flex flex-wrap gap-4">
          {(['ok', 'in_review', 'attention', 'none'] as ObligationState[]).map((state) => {
            const meta = STATE_META[state];
            return <span key={state} className="inline-flex items-center gap-1"><meta.Icon className={`h-3.5 w-3.5 ${meta.className}`} aria-hidden="true" />{meta.label}</span>;
          })}
        </span>
        {rows.length > SUPPLIER_LIMIT && (
          <button type="button" className={linkClass} onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Mostrar menos' : `Mostrar los ${number(rows.length)}`}
          </button>
        )}
      </div>
    </Card>
  );
}

function Upcoming({ data }: { data: ComplianceSummary }) {
  const rows = data.upcoming.filter((u) => u.missing > 0).slice(0, 8);
  return (
    <Card labelledBy="upcoming-title" title="Próximas fechas límite" subtitle={`Próximos ${UPCOMING_WINDOW_DAYS} días`}>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-600">Todos los proveedores ya entregaron lo que vence en este periodo.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((u) => (
            <li key={u.key} className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-3 px-5 py-3">
              <span className="rounded-lg bg-slate-100 px-1 py-1.5 text-center text-xs font-semibold uppercase leading-tight text-slate-700">{dayMonth(u.date)}</span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{u.definitionName} <span className="font-normal text-slate-500">· {u.period}</span></p>
                <p className="mt-0.5 text-xs text-slate-500">
                  <span className="font-semibold text-slate-900">{number(u.missing)}</span> de {number(u.applicable)} sin entregar · {u.obligations.map((o) => OBLIGATION_BY_ID[o].short).join(', ')}
                </p>
                <div className="mt-1.5"><Meter value={u.applicable - u.missing} max={u.applicable} tone="good" /></div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Problems({ data }: { data: ComplianceSummary }) {
  return (
    <Card labelledBy="problems-title" title="Documentos con más rechazos" subtitle="Últimos 180 días">
      {data.problems.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-600">Sin rechazos en este periodo.</p>
      ) : (
        <ul className="space-y-3 px-5 py-4">
          {data.problems.map((p) => {
            const rate = Math.round((p.rejected / p.total) * 100);
            return (
              <li key={p.definitionName}>
                <div className="mb-1 flex justify-between gap-3 text-xs">
                  <span className="truncate text-slate-700">{p.definitionName}</span>
                  <span className="shrink-0 tabular-nums text-slate-500"><span className="font-semibold text-slate-900">{rate}%</span> · {number(p.rejected)} de {number(p.total)}</span>
                </div>
                <Meter value={p.rejected} max={p.total} tone={rate >= 20 ? 'bad' : 'watch'} />
              </li>
            );
          })}
        </ul>
      )}
      <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">Un rechazo alto suele indicar instrucciones poco claras: ajústalas en <Link to="/admin/definitions" className={linkClass}>Definiciones</Link>.</p>
    </Card>
  );
}

function Responsiveness({ data }: { data: ComplianceSummary }) {
  const { rejections, resubmitted, medianDays } = data.responsiveness;
  const never = rejections - resubmitted;
  return (
    <Card labelledBy="response-title" title="Respuesta de proveedores" subtitle="Tras un rechazo · últimos 180 días">
      <dl className="grid grid-cols-2 gap-px bg-slate-200">
        <div className="bg-white p-5">
          <dt className="text-xs text-slate-500">Mediana para reenviar</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{medianDays == null ? '—' : daysLabel(Math.round(medianDays))}</dd>
        </div>
        <div className="bg-white p-5">
          <dt className="text-xs text-slate-500">Sin reenviar</dt>
          <dd className={`mt-1 text-2xl font-semibold tabular-nums ${never > 0 ? 'text-red-700' : 'text-slate-900'}`}>{number(never)}</dd>
          <dd className="text-xs text-slate-500">de {number(rejections)} rechazos</dd>
        </div>
      </dl>
      <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">Sin reenvío, el periodo queda sin evidencia.</p>
    </Card>
  );
}

function Coverage({ coverage, organizations }: { coverage: AsyncState<OrganizationCoverage[]>; organizations: Set<string> }) {
  const year = new Date().getFullYear();
  // Only organizations with active, non-internal suppliers (hides demo/test tenants).
  const rows = (coverage.data || []).filter((org) => organizations.has(org.name));
  return (
    <Card
      labelledBy="coverage-title"
      title="Cobertura por organización"
      subtitle={`Enero–${new Date().toLocaleDateString('es-MX', { month: 'long' })} ${year}`}
      action={<Link to="/report" className={`${linkClass} text-sm`}>Reporte detallado</Link>}
    >
      <DataState loading={coverage.loading} error={coverage.error} retry={coverage.reload} rows={2} />
      {!coverage.loading && coverage.data && (
        <ul className="divide-y divide-slate-100 px-5">
          {rows.map((org) => (
            <li key={org.name} className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:items-center sm:gap-6">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{org.name}</p>
                <p className="text-xs text-slate-500">{number(org.users)} proveedores</p>
              </div>
              {org.percentage == null ? <span className="text-sm text-slate-500">No disponible</span> : (
                <div className="flex items-center gap-3">
                  <div className="flex-1"><Meter value={org.percentage} max={100} tone={org.percentage >= 90 ? 'good' : org.percentage >= 70 ? 'watch' : 'bad'} /></div>
                  <span className="w-14 text-right text-sm font-semibold tabular-nums text-slate-900">{org.percentage.toFixed(1)}%</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function LegalNote() {
  return (
    <details className="group rounded-xl border border-slate-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-4 text-sm font-semibold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600">
        <Scale className="h-4 w-4 text-slate-500" aria-hidden="true" />
        ¿Por qué se agrupa así?
        <span className="ml-auto text-xs font-normal text-slate-500 group-open:hidden">Ver fundamento</span>
      </summary>
      <div className="border-t border-slate-200 px-5 py-4">
        <p className="max-w-3xl text-sm leading-relaxed text-slate-600">
          Al contratar servicios especializados, tu empresa responde solidariamente por las obligaciones laborales y de seguridad social del proveedor, y solo puede deducir los pagos y acreditar el IVA si conserva su evidencia de cumplimiento. Cada documento se clasifica según la obligación que respalda:
        </p>
        <dl className="mt-4 grid gap-4 md:grid-cols-2">
          {OBLIGATIONS.filter((o) => o.id !== 'otros').map((o) => (
            <div key={o.id}>
              <dt className="text-sm font-medium text-slate-900">{o.title} <span className="font-normal text-slate-500">· {o.legalBasis}</span></dt>
              <dd className="mt-1 text-xs leading-relaxed text-slate-600">{o.consequence}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 text-xs text-slate-500">Referencia informativa; no constituye asesoría legal ni fiscal. Valida los criterios con tu asesor.</p>
      </div>
    </details>
  );
}

export default function ComplianceView({ compliance, coverage }: { compliance: AsyncState<ComplianceSummary>; coverage: AsyncState<OrganizationCoverage[]> }) {
  const [params, setParams] = useSearchParams();
  const obligation = OBLIGATIONS.some((o) => o.id === params.get('obligacion')) ? (params.get('obligacion') as ObligationId) : null;
  const organization = params.get('org') || '';
  const [query, setQuery] = useState('');
  const setParam = (name: string, value: string | null) =>
    setParams((current) => { const next = new URLSearchParams(current); if (value) next.set(name, value); else next.delete(name); return next; }, { replace: true });
  const setObligation = (value: ObligationId | null) => setParam('obligacion', value);
  const setOrganization = (value: string) => setParam('org', value || null);
  const returnTo = `/admin/dashboard?${new URLSearchParams(Array.from(params.entries()).filter(([k]) => ['vista', 'obligacion', 'org'].includes(k)))}`;
  const data = compliance.data;
  const organizations = useMemo(
    () => Array.from(new Set((data?.suppliers || []).map((s) => s.organization || 'Sin organización'))).sort((a, b) => a.localeCompare(b, 'es')),
    [data],
  );
  const rows = useMemo(() => {
    const text = query.trim().toLocaleLowerCase('es-MX');
    return (data?.suppliers || []).filter((s) =>
      (!organization || (s.organization || 'Sin organización') === organization) &&
      (!obligation || s.obligations[obligation] === 'attention') &&
      (!text || s.name.toLocaleLowerCase('es-MX').includes(text) || s.email.toLocaleLowerCase('es-MX').includes(text)));
  }, [data, organization, obligation, query]);

  if (compliance.loading || compliance.error || !data)
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <DataState loading={compliance.loading} error={compliance.error} retry={compliance.reload} rows={5} />
      </div>
    );
  return (
    <div className="space-y-6">
      <Exposure data={data} total={data.suppliers.length} selected={obligation} onSelect={setObligation} />
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-600">
          Organización
          <select className={`${controlClass} mt-1 block min-w-[180px]`} value={organization} onChange={(e) => setOrganization(e.target.value)}>
            <option value="">Todas</option>
            {organizations.map((name) => <option key={name}>{name}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          Buscar proveedor
          <input type="search" className={`${controlClass} mt-1 block w-64 max-w-full`} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nombre o correo" />
        </label>
        {(obligation || organization || query) && (
          <button type="button" className={`${linkClass} mb-2 text-sm`} onClick={() => { setParams((current) => { const next = new URLSearchParams(current); next.delete('obligacion'); next.delete('org'); return next; }, { replace: true }); setQuery(''); }}>Limpiar filtros</button>
        )}
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.9fr)_minmax(300px,1fr)]">
        <SupplierTable rows={rows} filter={obligation} returnTo={returnTo} />
        <div className="min-w-0 space-y-6">
          <Upcoming data={data} />
          <Responsiveness data={data} />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Problems data={data} />
        <Coverage coverage={coverage} organizations={new Set(organizations)} />
      </div>
      <LegalNote />
    </div>
  );
}
